import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Container,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Link as RouterLink } from "react-router-dom";

const headerIconFrameSx = {
  width: 72,
  height: 72,
  borderRadius: 2,
  overflow: "hidden",
  flexShrink: 0,
};

const headerTitleSx = {
  fontWeight: 700,
  fontSize: { xs: "2rem", md: "3rem" },
  lineHeight: 1,
};

const faqPanelSx = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 2,
  "&:before": { display: "none" },
};

const feedbackRows = [
  {
    issue:
      "Project selection in the main menu would be better off using project no. assigned by individual company admin, so it is easier to search jobs rather than a generic contract no. or name.",
    category: "datamodel, search",
    expected:
      "Enable text search in ID or primary key tables. Enable gallery UI cards with more info such as job number and district. Consider map or location search.",
  },
  {
    issue:
      "During startup, the project page can appear empty even when projects were input before. Allow offline mode to enter and cache data, then sync when online.",
    category: "datastore, cloudsync",
    expected:
      "Ensure project data is available on startup in offline environments. Cache user input fully and import cloud-synced updates from other devices in the same project or borehole. Add pull-to-refresh for project updates.",
  },
];

const FAQView = () => {
  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: 4 }}>
        <Stack spacing={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <Box sx={headerIconFrameSx}>
              <Box
                component="img"
                alt="SmartLog"
                src="assets/logos/SmartLog.png"
                sx={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </Box>
            <Box>
              <Typography variant="h3" sx={headerTitleSx}>
                FAQ
              </Typography>
              <Typography color="text.secondary">
                Common questions about SmartLog onboarding, access, feedback,
                and GI data sharing.
              </Typography>
            </Box>
          </Box>

          <Accordion defaultExpanded disableGutters elevation={0} sx={faqPanelSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700 }}>How do I get started?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Typography color="text.secondary">
                  Begin by installing the app via Android APK or iOS TestFlight
                  from the <Link component={RouterLink} to="/releases">Releases</Link> page.
                  On app startup, select the sign-up button and register using
                  your work details.
                </Typography>
                <Typography color="text.secondary">
                  Please ensure you use a valid email address so the forgot
                  password function will work when needed.
                </Typography>
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded disableGutters elevation={0} sx={faqPanelSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700 }}>
                How can I provide feedback about SmartLog?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Typography color="text.secondary">
                  Please follow the table format below and send your feedback to{" "}
                  <Link href="mailto:caleb.sang@arup.com">caleb.sang@arup.com</Link>.
                </Typography>

                <TableContainer sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                  <Table size="small" aria-label="Feedback format example">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Comment or Problematic Behavior</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: "25%" }}>Category</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Expected Behavior</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {feedbackRows.map((row, index) => (
                        <TableRow key={index}>
                          <TableCell>{row.issue}</TableCell>
                          <TableCell>{row.category}</TableCell>
                          <TableCell>{row.expected}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography color="text.secondary">
                  We have quantified feedback items so far into categories such
                  as datamodel, UI, UX, bug, automation, lithology, users,
                  webapp, workflow, access, architecture, domain, insert,
                  prompt, SPT, blob, cloudsync, crash, datastore, driller,
                  search, security, and validation.
                </Typography>
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded disableGutters elevation={0} sx={faqPanelSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700 }}>
                How can I change my login credentials?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="text.secondary">
                Forgetting your password is a common issue. For security,
                password reset requires the same email address used for account
                sign-up. If it matches, you will receive a reset email. Please
                note that passwords are case sensitive.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded disableGutters elevation={0} sx={faqPanelSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700 }}>
                How can I share GI data with others?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="text.secondary">
                The primary purpose of SmartLog is to generate and export GI
                data as final output. You can import exported .ags(4) or .xlsx
                files into gINT or other compatible logging software using your
                correspondence file and data libraries. The Excel version is
                exported together with AGS4 to remain open-source friendly.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded disableGutters elevation={0} sx={faqPanelSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700 }}>
                Why can&apos;t I input Driller&apos;s input data as a Geologist?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="text.secondary">
                This is related to user-level access control. Different roles
                (GFT, Drillers, Geologists, and Project Admin) have different
                data input permissions, so each user can input only their
                designated section. Unless you are assigned Project Admin, some
                input options will be limited. Project creators are Project
                Admin by default and can assign that role to other members in
                the project members page.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded disableGutters elevation={0} sx={faqPanelSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700 }}>
                When will the application be deployed as a GI contractual requirement?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="text.secondary">
                Deployment timelines for contractual adoption will be announced
                once officially confirmed.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Stack>
      </Paper>
    </Container>
  );
};

export default FAQView;
